import React, { useState } from 'react'
import { useMutation } from 'react-query'
import { AxiosError } from 'axios'

import { Divider, Grid, Typography } from '@material-ui/core'
import { DropzoneArea } from 'material-ui-dropzone'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import DialogTitle from '@material-ui/core/DialogTitle'

import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import Paper from '@material-ui/core/Paper'

import { skuit } from 'src/services/skuit'
import toast from 'react-hot-toast'
import { Notification } from 'src/components/Notification'

const createData = (field: string, description: string, requirements: string) => {
  return { field, description, requirements }
}

const rows = [
  createData('number', 'Order number', 'Required'),
  createData('details', 'Order details', 'Not Required'),
  createData('consignee', 'Name of who will be receiving the order', 'Required'),

  createData('company', 'Company name', 'Not required'),
  createData('phone', 'Phone number', 'Not required'),
  createData('address1', 'First line of the recipient address', 'Required'),
  createData('address2', 'Second line of the recipient address', 'Not required'),
  createData('address3', 'Third line of the recipient address', 'Not required'),
  createData('city', 'City of the recipient address', 'Required'),
  createData('state', 'State of the recipient address', 'Required'),
  createData('postal_code', 'Postal code of the recipient address', 'Required'),
  createData('sku', 'SKU to add to the specified order', 'Required'),
  createData('num_units', 'Number of units of the specified SKU to order', 'Required'),
  createData(
    'backorder',
    'Boolean (true/false) indicating if we should allow backordering for this SKU',
    'Not required (default false)'
  ),
]

const ExpectedFormatTable = () => (
  <TableContainer component={Paper}>
    <Table aria-label="expected format">
      <TableHead>
        <TableRow>
          <TableCell>Field </TableCell>
          <TableCell align="left">Description</TableCell>
          <TableCell align="left">Requirements</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.field}>
            <TableCell component="th" scope="row">
              {row.field}
            </TableCell>
            <TableCell align="left">{row.description}</TableCell>
            <TableCell align="left">{row.requirements}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
)

interface BulkOrderUploadDialogProps {
  open: boolean
  handleClose: () => void
  onSuccess: () => void
}

type ErrorResponse = {
  detail: string
}

export const BulkOrderUploadDialog = (props: BulkOrderUploadDialogProps) => {
  const { open, handleClose, onSuccess } = props

  const [selectedFile, setSelectedFile] = useState<File | undefined>()
  const [bulkErrors, setBulkErrors] = useState<any[]>([])
  const [detailError, setDetailError] = useState<ErrorResponse | null>(null)

  const { mutate: doUpload } = useMutation(skuit.client.parcel.orders.uploadCsv, {
    onSuccess: () => {
      onSuccess()
      toast.success(() => <Notification message={`Successfully created Orders from csv`} />)

      handleClose()
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === 500) {
        setBulkErrors([])
      } else {
        if (error.response?.data.detail) {
          setDetailError(error.response.data)
        } else {
          setBulkErrors(error.response?.data)
        }
      }

      toast.success(() => <Notification message={`Failed to process CSV: ${error}`} />)
    },
  })

  const uploadCsv = (file: File) => {
    if (file) {
      setBulkErrors([])
      const data = { file }
      doUpload({ data })
    }
  }

  return (
    <div>
      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title"> Bulk Order Upload </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Grid container spacing={2}>
              <Grid item>Expected Format</Grid>
              <Grid item>{ExpectedFormatTable()}</Grid>
              <Grid item container>
                {!!bulkErrors && !!bulkErrors.length && (
                  <Grid item xs={12}>
                    <Typography color="error">Errors in CSV:</Typography>
                    {bulkErrors.map((error: any, index: number) => (
                      <Typography key={index} color="error">
                        Line {error.line}:{' '}
                        {JSON.stringify(error.details)}
                      </Typography>
                    ))}
                  </Grid>
                )}
                {!!detailError && (
                  <Grid item xs={12}>
                    <Typography color="error">{detailError.detail}</Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>
              <Typography>
                To specify multiple SKUs for an order, create additional rows with the order
                information duplicated. Note that any conflicting data (other than the Order Number)
                will be ignored. The first set of information for an Order Number will be used.
              </Typography>
            </Grid>
          </DialogContentText>
          <DropzoneArea
            dropzoneText="Drag and drop or click to add file"
            acceptedFiles={['.csv']}
            onChange={(loadedFiles) => {
              setSelectedFile(loadedFiles[0])
            }}
            filesLimit={1}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <div>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                if (selectedFile) {
                  uploadCsv(selectedFile)
                }
              }}
            >
              Create Orders
            </Button>
          </div>
          <Divider variant="middle" />
        </DialogActions>
      </Dialog>
    </div>
  )
}
